import React, { useState, useRef, useCallback } from 'react';
import { ErrorBox } from './common/ErrorBox';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { useFormContext } from 'react-hook-form';
import { InfoModal } from '../components/InfoModal';

const FixedValuePicker = ({
  name,
  label,
  title,
  errors,
  detailed_label,
  validation,
  defaultValue,
  min = 0,
  max = 100,
  step = 10,
  unit = '',
  displayUnit = null,  // Unit to show to user (e.g., 'm')
  conversionFactor = 1,  // Factor to convert display to backend (e.g., 1000 for m to mm)
  description
}) => {
  const { register, setValue, getValues } = useFormContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Convert backend value to display value
  const backendToDisplay = useCallback((value) => {
    return value / conversionFactor;
  }, [conversionFactor]);

  // Convert display value to backend value
  const displayToBackend = useCallback((value) => {
    return value * conversionFactor;
  }, [conversionFactor]);

  // Get initial value from form (in backend units) and convert to display units
  const initialBackendValue = getValues(name) || defaultValue || min;

  // Local state for display value (what the slider uses internally - backend units)
  const [backendValue, setBackendValue] = useState(initialBackendValue);
  const isDraggingRef = useRef(false);

  // Calculate the number of steps
  const totalSteps = Math.floor((max - min) / step);

  // Determine which unit to show
  const effectiveDisplayUnit = displayUnit || unit;

  // Handle slider input - only update local state during dragging
  const handleSliderInput = useCallback((e) => {
    isDraggingRef.current = true;
    const stepIndex = parseInt(e.target.value);
    const value = min + (stepIndex * step);
    setBackendValue(value);
  }, [min, step]);

  // Handle when slider is released - sync with form and validate
  const handleSliderChange = useCallback((e) => {
    const stepIndex = parseInt(e.target.value);
    const value = min + (stepIndex * step);
    setBackendValue(value);
    setValue(name, value, { shouldValidate: true });
    isDraggingRef.current = false;
  }, [name, setValue, min, step]);

  // Calculate current step index for slider
  const currentStepIndex = Math.round((backendValue - min) / step);

  // Calculate display value for showing to user
  const displayValue = backendToDisplay(backendValue);

  // Format display value for better readability
  const formattedDisplayValue = conversionFactor !== 1
    ? displayValue.toFixed(1).replace(/\.0$/, '') // Remove .0 if it's a whole number
    : displayValue;

  return (
    <>
      <div className="bg-white/60 rounded-2xl shadow-sm border border-gray-200/60 p-3.5 transition-all duration-200 hover:bg-white/80 hover:shadow-md backdrop-blur-sm">
        <div className="flex justify-between items-start mb-2">
          <h2 className='text-base font-semibold text-gray-900'>{title}</h2>
          <AiOutlineInfoCircle
            className="text-gray-600 text-base cursor-pointer hover:text-gray-800 transition-colors flex-shrink-0"
            onClick={() => setIsModalOpen(true)}
          />
        </div>

        <p className="text-sm text-gray-600 mb-3">{detailed_label}</p>

        <div className="space-y-3">
          {/* Display current value */}
          <div className="flex justify-start items-center">
            <span className="text-2xl font-bold text-gray-900">
              {formattedDisplayValue}{effectiveDisplayUnit}
            </span>
          </div>

          {/* Slider */}
          <div className="relative">
            <input
              type="range"
              min="0"
              max={totalSteps}
              value={currentStepIndex}
              onInput={handleSliderInput}
              onChange={handleSliderChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb"
              style={{
                background: `linear-gradient(to right, rgb(31, 41, 55) 0%, rgb(31, 41, 55) ${(currentStepIndex / totalSteps) * 100}%, rgb(229, 231, 235) ${(currentStepIndex / totalSteps) * 100}%, rgb(229, 231, 235) 100%)`
              }}
            />

            {/* Min and Max labels */}
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{(backendToDisplay(min)).toFixed(1).replace(/\.0$/, '')}{effectiveDisplayUnit}</span>
              <span>{(backendToDisplay(max)).toFixed(1).replace(/\.0$/, '')}{effectiveDisplayUnit}</span>
            </div>
          </div>

          {/* Hidden input for form registration */}
          <input
            type="hidden"
            {...register(name, validation)}
            value={backendValue}
          />
        </div>

        <ErrorBox errors={errors} />
      </div>

      <InfoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={title}
      >
        <p className="text-sm leading-relaxed">{description}</p>
      </InfoModal>
    </>
  );
};

export default FixedValuePicker;
